/**
 * Role-Based Route Guards
 * Ensures users can only access routes allowed for their roles
 */
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Generic Role Route - checks if user has any of the allowed roles
 */
export const RoleRoute = ({ children, allowedRoles, redirectTo = '/dashboard' }) => {
  const { isAuthenticated, loading, user, hasAnyRole } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasAnyRole(allowedRoles)) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

/**
 * Driver Route - requires DRIVER role
 */
export const DriverRoute = ({ children }) => {
  return (
    <RoleRoute allowedRoles={['DRIVER', 'ADMIN', 'CEO']}>
      {children}
    </RoleRoute>
  );
};

/**
 * PUDO Route - requires PUDO_POINT or PUDO_STAFF role
 */
export const PudoRoute = ({ children }) => {
  return (
    <RoleRoute allowedRoles={['PUDO_POINT', 'PUDO_OPERATOR', 'PUDO_STAFF', 'ADMIN', 'CEO']}>
      {children}
    </RoleRoute>
  );
};

/**
 * Merchant Route - requires BUSINESS_MERCHANT role
 */
export const MerchantRoute = ({ children }) => {
  return (
    <RoleRoute allowedRoles={['BUSINESS_MERCHANT', 'MERCHANT', 'ADMIN', 'CEO']}>
      {children}
    </RoleRoute>
  );
};

/**
 * Fulfillment Route - for order fulfillment (merchant or fulfillment staff)
 */
export const FulfillmentRoute = ({ children }) => {
  return (
    <RoleRoute allowedRoles={['BUSINESS_MERCHANT', 'MERCHANT', 'FULFILLMENT', 'ADMIN', 'CEO']}>
      {children}
    </RoleRoute>
  );
};

export default RoleRoute;
